import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import RecordUsage from 'components/record-shell/RecordUsage';
import { api } from 'services/api';
import history from 'services/history';
import { Product } from 'types/product';
import ProductForm from '../form/ProductForm';

const EditProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [savedName, setSavedName] = useState('');

  const loadProduct = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get<Product>(`/products/${id}`)
      .then(response => {
        setProduct(response.data);
        setSavedName(response.data.name);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do produto');
        history.push('/products');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadProduct().finally(() => setLoading(false));
  }, [loadProduct]);

  function goBack() {
    history.push('/products');
  }

  function handleChange<K extends keyof Product>(index: K, value: Product[K]) {
    setProduct(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!product) return;

    setSaving(true);

    api
      .put(`/products/${id}`, { ...product, unit: product.unit || 'un' })
      .then(() => loadProduct())
      .then(() => toast.success('Produto atualizado'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o produto');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!product) return;

    setValidation('');
    if (!product.name) {
      setValidation('name');
      return;
    }
    if (!product.manufacturer) {
      setValidation('manufacturer');
      return;
    }
    handleSubmit();
  }

  // descarta o que foi digitado e recarrega o cadastro
  function handleCancel() {
    setValidation('');
    setLoading(true);
    loadProduct().finally(() => setLoading(false));
  }

  function handleDelete() {
    setSaving(true);

    api
      .delete(`/products/${id}`)
      .then(() => {
        toast.success('Produto excluído com sucesso!');
        history.push('/products');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível excluir o produto');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  const usage = product?.usage;
  const inUse = !!usage && usage.packages + usage.procedures + usage.suppliers > 0;

  return (
    <>
      <Appbar title="Produtos" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          title="Deseja excluir este produto?"
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleDelete}
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Produto'} backAction={goBack}>
          {product?.unit && <Chip size="small" variant="outlined" label={product.unit} />}
          {product?.manufacturer && <Chip size="small" variant="outlined" label={product.manufacturer} />}
        </RecordHeader>

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !product ? (
              <NoData message="Não foi possível carregar os dados do produto" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <ProductForm handleChange={handleChange} product={product} validation={validation} />

                {usage && (
                  <RecordUsage
                    title="Onde é usado"
                    items={[
                      { count: usage.packages, singular: 'pacote', plural: 'pacotes' },
                      { count: usage.procedures, singular: 'procedimento', plural: 'procedimentos' },
                      { count: usage.suppliers, singular: 'fornecedor', plural: 'fornecedores' },
                    ]}
                    blockedMessage="Este produto está em uso e não pode ser excluído. Remova-o desses cadastros antes."
                    freeMessage="Nenhum cadastro usa este produto, então ele pode ser excluído."
                  />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && product && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving || inUse} onClick={() => setConfirmDialogOpen(true)}>
              Excluir produto
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditProduct;
